import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useSceneStore, useSelectionStore } from '../../stores';
import { SceneObject } from '../../types';

interface Vector3InputProps {
  label: string;
  value: [number, number, number];
  onChange: (value: [number, number, number]) => void;
  step?: number;
  min?: number;
  max?: number;
}

const Vector3Input: React.FC<Vector3InputProps> = ({ label, value, onChange, step = 0.1, min, max }) => {
  const handleChange = (index: number, newValue: string) => {
    const numValue = parseFloat(newValue) || 0;
    const clampedValue = min !== undefined && max !== undefined ? Math.max(min, Math.min(max, numValue)) : numValue;

    const newVector: [number, number, number] = [...value];
    newVector[index] = clampedValue;
    onChange(newVector);
  };

  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium">{label}</Label>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-xs text-gray-500">X</Label>
          <Input
            type="number"
            value={value[0].toFixed(2)}
            onChange={(e) => handleChange(0, e.target.value)}
            step={step}
            min={min}
            max={max}
            className="h-8 text-xs"
          />
        </div>
        <div>
          <Label className="text-xs text-gray-500">Y</Label>
          <Input
            type="number"
            value={value[1].toFixed(2)}
            onChange={(e) => handleChange(1, e.target.value)}
            step={step}
            min={min}
            max={max}
            className="h-8 text-xs"
          />
        </div>
        <div>
          <Label className="text-xs text-gray-500">Z</Label>
          <Input
            type="number"
            value={value[2].toFixed(2)}
            onChange={(e) => handleChange(2, e.target.value)}
            step={step}
            min={min}
            max={max}
            className="h-8 text-xs"
          />
        </div>
      </div>
    </div>
  );
};

interface ColorInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
}

const ColorInput: React.FC<ColorInputProps> = ({ label, value, onChange }) => {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium">{label}</Label>
      <div className="flex items-center space-x-2">
        <Input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-12 h-8 p-1 border rounded"
        />
        <Input type="text" value={value} onChange={(e) => onChange(e.target.value)} className="flex-1 h-8 text-xs" />
      </div>
    </div>
  );
};

interface ObjectPropertiesProps {
  object: SceneObject;
}

const ObjectProperties: React.FC<ObjectPropertiesProps> = ({ object }) => {
  const { updateObject } = useSceneStore();

  const handlePropertyChange = (property: string, value: any) => {
    updateObject(object.id, { [property]: value });
  };

  const handleNestedPropertyChange = (path: string[], value: any) => {
    const updatedProperties = { ...object.properties };
    let current = updatedProperties;

    for (let i = 0; i < path.length - 1; i++) {
      if (!current[path[i]]) current[path[i]] = {};
      current = current[path[i]];
    }

    current[path[path.length - 1]] = value;
    updateObject(object.id, { properties: updatedProperties });
  };

  const renderDeviceProperties = () => {
    const props = object.properties as any;
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Device Properties</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs font-medium">Device Type</Label>
            <Input
              value={props.deviceType || ''}
              onChange={(e) => handleNestedPropertyChange(['deviceType'], e.target.value)}
              className="h-8 text-xs mt-1"
              disabled
            />
          </div>

          <div>
            <Label className="text-xs font-medium">Status</Label>
            <select
              value={props.status || 'online'}
              onChange={(e) => handleNestedPropertyChange(['status'], e.target.value)}
              className="w-full h-8 text-xs border rounded px-2 mt-1"
            >
              <option value="online">Online</option>
              <option value="offline">Offline</option>
              <option value="warning">Warning</option>
              <option value="error">Error</option>
            </select>
          </div>

          {props.color && (
            <ColorInput
              label="Color"
              value={props.color}
              onChange={(value) => handleNestedPropertyChange(['color'], value)}
            />
          )}

          {props.data && (
            <div>
              <Label className="text-xs font-medium">Sensor Data</Label>
              <div className="text-xs text-gray-600 mt-1">
                <div>
                  Value: {props.data.value} {props.data.unit}
                </div>
                <div>Last Update: {new Date(props.data.timestamp).toLocaleTimeString()}</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderPrimitiveProperties = () => {
    const props = object.properties as any;
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Primitive Properties</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs font-medium">Geometry</Label>
            <select
              value={props.geometry || 'box'}
              onChange={(e) => handleNestedPropertyChange(['geometry'], e.target.value)}
              className="w-full h-8 text-xs border rounded px-2 mt-1"
            >
              <option value="box">Box</option>
              <option value="sphere">Sphere</option>
              <option value="cylinder">Cylinder</option>
              <option value="plane">Plane</option>
            </select>
          </div>

          {props.material && (
            <div className="space-y-3">
              <Label className="text-xs font-medium">Material</Label>

              <ColorInput
                label="Color"
                value={props.material.color || '#ffffff'}
                onChange={(value) => handleNestedPropertyChange(['material', 'color'], value)}
              />

              <div>
                <Label className="text-xs font-medium">
                  Metalness: {props.material.metalness?.toFixed(2) || '0.00'}
                </Label>
                <Slider
                  value={[props.material.metalness || 0]}
                  onValueChange={([value]) => handleNestedPropertyChange(['material', 'metalness'], value)}
                  max={1}
                  step={0.01}
                  className="mt-2"
                />
              </div>

              <div>
                <Label className="text-xs font-medium">
                  Roughness: {props.material.roughness?.toFixed(2) || '0.00'}
                </Label>
                <Slider
                  value={[props.material.roughness || 0]}
                  onValueChange={([value]) => handleNestedPropertyChange(['material', 'roughness'], value)}
                  max={1}
                  step={0.01}
                  className="mt-2"
                />
              </div>

              {props.material.transparent !== undefined && (
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={props.material.transparent || false}
                    onCheckedChange={(checked) => handleNestedPropertyChange(['material', 'transparent'], checked)}
                  />
                  <Label className="text-xs">Transparent</Label>
                </div>
              )}

              {props.material.opacity !== undefined && (
                <div>
                  <Label className="text-xs font-medium">Opacity: {props.material.opacity?.toFixed(2) || '1.00'}</Label>
                  <Slider
                    value={[props.material.opacity || 1]}
                    onValueChange={([value]) => handleNestedPropertyChange(['material', 'opacity'], value)}
                    max={1}
                    step={0.01}
                    className="mt-2"
                  />
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4">
      {/* Basic Properties */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Basic Properties</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs font-medium">Name</Label>
            <Input
              value={object.name}
              onChange={(e) => handlePropertyChange('name', e.target.value)}
              className="h-8 text-xs mt-1"
            />
          </div>

          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Switch
                checked={object.visible}
                onCheckedChange={(checked) => handlePropertyChange('visible', checked)}
              />
              <Label className="text-xs">Visible</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Switch checked={object.locked} onCheckedChange={(checked) => handlePropertyChange('locked', checked)} />
              <Label className="text-xs">Locked</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transform Properties */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Transform</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Vector3Input
            label="Position"
            value={object.position}
            onChange={(value) => handlePropertyChange('position', value)}
            step={0.1}
          />

          <Vector3Input
            label="Rotation (degrees)"
            value={object.rotation.map((r) => (r * 180) / Math.PI) as [number, number, number]}
            onChange={(value) =>
              handlePropertyChange(
                'rotation',
                value.map((r) => (r * Math.PI) / 180),
              )
            }
            step={1}
          />

          <Vector3Input
            label="Scale"
            value={object.scale}
            onChange={(value) => handlePropertyChange('scale', value)}
            step={0.1}
            min={0.01}
          />
        </CardContent>
      </Card>

      {/* Type-specific properties */}
      {object.type === 'device' && renderDeviceProperties()}
      {object.type === 'primitive' && renderPrimitiveProperties()}
    </div>
  );
};

export const PropertyPanel: React.FC = () => {
  const { selectedObjectIds } = useSelectionStore();
  const { getObjectById } = useSceneStore();

  if (selectedObjectIds.length === 0) {
    return (
      <div className="p-4 text-center text-gray-500">
        <p className="text-sm">No object selected</p>
        <p className="text-xs mt-2">Select an object to view its properties</p>
      </div>
    );
  }

  if (selectedObjectIds.length > 1) {
    return (
      <div className="p-4">
        <h3 className="text-sm font-semibold mb-4">Multiple Objects Selected</h3>
        <p className="text-xs text-gray-500 mb-4">
          {selectedObjectIds.length} objects selected. Multi-object editing coming soon.
        </p>
        <Button size="sm" className="w-full">
          Apply to All
        </Button>
      </div>
    );
  }

  const object = getObjectById(selectedObjectIds[0]);

  if (!object) {
    return (
      <div className="p-4 text-center text-gray-500">
        <p className="text-sm">Object not found</p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-full">
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold">Properties</h3>
          <span className="text-xs text-gray-500 capitalize">{object.type}</span>
        </div>

        <ObjectProperties object={object} />
      </div>
    </ScrollArea>
  );
};
